import type { MemoryOrigin } from "@/features/material-compliance/api/types"
import type { TranslationKeys } from "@/hooks/useTranslation/catalogs"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"

import { formatMemoryDate } from "./MemoryTrail"
import { renderMemorySource } from "./memorySourceLink"

export type MemoryBecauseSurface = "concentration" | "supplier" | "answer"

/**
 * Which "who" phrase this surface uses for each trust class. Every surface names a
 * different thing (a correction, a name mapping, an answer), so the subject of the
 * sentence is necessarily surface-specific — this is the one place that mapping lives.
 *
 * NOT "you" VS "a colleague" HERE, DELIBERATELY. This glance line is the thing a reviewer
 * sees on every suggestion, whether they open anything or not — naming a specific colleague
 * (even generically) on something that is not blame reads as exactly that: blame, for a
 * correction that may well be correct. Decided after testing: keep the box itself neutral
 * ("a previous correction", regardless of who), and say who it actually was only in
 * `MemoryTrail`'s collapsible detail, which a reviewer opens on purpose. `is_you` still
 * matters there, just not here — see `MemoryTrail`'s own `who` line.
 */
const WHO: Record<
  MemoryBecauseSurface,
  { correction: TranslationKeys; other: TranslationKeys }
> = {
  concentration: {
    correction: "MC_MEMORY_WHO_CORRECTION_GENERIC",
    other: "MC_MEMORY_WHO_CORRECTION_CONFIRMED",
  },
  supplier: {
    correction: "MC_MEMORY_WHO_SUPPLIER_GENERIC",
    other: "MC_MEMORY_WHO_SUPPLIER_MATCHED",
  },
  answer: {
    correction: "MC_MEMORY_WHO_ANSWER_GENERIC",
    other: "MC_MEMORY_WHO_ANSWER_CONFIRMED",
  },
}

export interface MemoryBecauseProps {
  origin: MemoryOrigin | null | undefined
  surface: MemoryBecauseSurface
  /** The server's own sentence — English, with an English date. Used ONLY when `origin`
   *  is absent, which the read path guarantees never happens alongside a real suggestion
   *  (see the note below). Keeping it is a safety net, not a second code path people are
   *  meant to exercise. */
  fallback: string | null
  testId: string
}

/**
 * The glance line under a suggestion — one sentence, always visible, in the reader's own
 * language. Replaces rendering the server's `because` string directly.
 *
 * WHY THIS EXISTED AS A SERVER STRING AT ALL, AND WHY THAT WAS WRONG. `because` is built in
 * `mc_memory/screens.py`, in English, with an English date (`%-d %b %Y`) — correct for the
 * person who wrote it, wrong for every other reader. A German account saw German labels
 * wrapped around an English sentence. `MemoryTrail` already fixed this for the collapsible
 * detail; this does the same for the line that is always on screen.
 *
 * SAFE TO BUILD FROM `origin` ALONE. `because` and `origin` are produced from the same
 * `provenance` object in `_shown_hint` (`usage.py`) — one exists exactly when the other
 * does. So preferring a localized render whenever `origin` is present, and falling back to
 * the raw string only when it is not, changes nothing about when a suggestion explains
 * itself; it only changes the language it does so in.
 *
 * NO TRANSLATED GLUE WORDS. The sentence is `who (source, when)` — parentheses, not a
 * preposition like "on" that would need a different, easy-to-get-wrong translation per
 * language and per case ("on" vs "from" vs nothing). Source and when are themselves already
 * locale-correct (a real link via `renderMemorySource`, a local-timezone date via
 * `formatMemoryDate`), so only the nine short "who" phrases need translating, and none of
 * them carry a grammar risk the way a stitched-together sentence would.
 */
export function MemoryBecause({
  origin,
  surface,
  fallback,
  testId,
}: MemoryBecauseProps) {
  const { translate, currentLanguage } = useTranslation()

  if (!origin) {
    return fallback ? (
      <p className="text-[11px] text-grey5">{fallback}</p>
    ) : null
  }

  const keys = WHO[surface]
  // TRUST CLASS STILL DECIDES, same reasoning as before: a confirmed/matched entry was
  // never anyone's deliberate act, so it reads as "confirmed", never as "a correction".
  const whoKey =
    origin.trustClass !== "explicit_correction" ? keys.other : keys.correction
  const who = translate(whoKey)

  const source = renderMemorySource(
    origin,
    translate("MC_MEMORY_OPEN_SOURCE"),
    testId,
  )
  const when = origin.occurredOn
    ? formatMemoryDate(origin.occurredOn, currentLanguage.toLowerCase())
    : null

  // Both pieces of detail are optional and independent — a source the entry's own row
  // was deleted from, or a date nothing ever recorded, must not break the sentence. If
  // neither is present, the parenthetical is simply absent.
  const detail: React.ReactNode[] = [source, when].filter(Boolean)

  return (
    <p
      className="text-[11px] text-grey5"
      data-testid={`memory-because-${testId}`}
    >
      {who}
      {detail.length > 0 ? (
        <>
          {" ("}
          {detail.map((part, i) => (
            <span key={i}>
              {i > 0 ? ", " : null}
              {part}
            </span>
          ))}
          {")"}
        </>
      ) : null}
    </p>
  )
}
